import { isEqual } from '@luminovo/commons';
import { ButtonGroup, ButtonGroupItem, colorSystem, Text, Tooltip } from '@luminovo/design-system';
import React from 'react';

import { ChartFrame } from './ChartFrame';
import { type ChartTransformer, resolveLabel } from './createChartTransformer';
import { StackedBarChart } from './StackedBarChart';
import { ChartLegend } from './StackedBarChart/ChartLegend';
import { type ChartDatum, type ChartTransformerResult, type StackedBarChartProps } from './types';

type TanStackChartProps<TData, TSharedContext> = {
    title: React.ReactNode;
    transformer: ChartTransformer<TData, TSharedContext>;
    data: TData[];
    sharedContext: TSharedContext;
    onBarClick?: (datum: ChartDatum<string>) => void;
    tooltipSubtitle?: StackedBarChartProps<string>['tooltipSubtitle'];
    /**
     * Renders a different chart body in place of the default bars, keeping the title,
     * group-by buttons and legend. Used by charts that plot the transformed data differently,
     * e.g. {@link TimeSeriesChart}.
     */
    renderChart?: (props: { result: ChartTransformerResult<string>; selectedKeys: string[] }) => React.ReactNode;
};

export function TanStackChart<TData, TSharedContext>({
    title,
    transformer,
    data,
    sharedContext,
    onBarClick,
    tooltipSubtitle,
    renderChart,
}: TanStackChartProps<TData, TSharedContext>) {
    const [groupByKey, setGroupByKey] = React.useState(transformer.groupByOptions[0]?.key ?? '');

    const prevTransformerRef = React.useRef(transformer);
    if (prevTransformerRef.current !== transformer) {
        prevTransformerRef.current = transformer;
        setGroupByKey(transformer.groupByOptions[0]?.key ?? '');
    }

    const result = React.useMemo(
        () => transformer.transform(data, groupByKey, sharedContext),
        [transformer, data, groupByKey, sharedContext],
    );

    // The legend lists only series that have data in this view; selecting them all again means "all".
    const legendKeys = React.useMemo(
        () => result.keys.filter((key) => result.data.some((datum) => Number(datum[key] ?? 0) !== 0)),
        [result],
    );
    const prevKeysRef = React.useRef<string[]>(legendKeys);
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>(legendKeys);

    if (!isEqual(prevKeysRef.current, legendKeys)) {
        // A filter can change which series have data: keep the user's choice where it still applies.
        const wasAll = selectedKeys.length === prevKeysRef.current.length;
        const kept = selectedKeys.filter((key) => legendKeys.includes(key));
        prevKeysRef.current = legendKeys;
        setSelectedKeys(wasAll || kept.length === 0 ? legendKeys : kept);
    }

    // The first click on a series shows it alone; later clicks add or remove series.
    const handleToggleKey = React.useCallback((key: string) => {
        setSelectedKeys((prev) => {
            const allKeys = prevKeysRef.current;
            if (prev.length === allKeys.length) {
                return [key];
            }
            if (prev.includes(key)) {
                const next = prev.filter((k) => k !== key);
                return next.length === 0 ? allKeys : next;
            }
            const next = [...prev, key];
            return next.length === allKeys.length ? allKeys : next;
        });
    }, []);

    const [onlyGroupBy] = transformer.groupByOptions;

    return (
        <ChartFrame
            title={title}
            controls={
                <>
                    {transformer.groupByOptions.length > 1 ? (
                        <ButtonGroup size="small">
                            {transformer.groupByOptions.map((g) => {
                                const button = (
                                    <ButtonGroupItem
                                        key={g.key}
                                        onClick={() => setGroupByKey(g.key)}
                                        selected={groupByKey === g.key}
                                    >
                                        {resolveLabel(g.label)}
                                    </ButtonGroupItem>
                                );
                                return g.tooltip ? (
                                    <Tooltip key={g.key} title={resolveLabel(g.tooltip)}>
                                        {button}
                                    </Tooltip>
                                ) : (
                                    button
                                );
                            })}
                        </ButtonGroup>
                    ) : (
                        onlyGroupBy && (
                            <Text variant="h4" color={colorSystem.neutral[7]}>
                                {resolveLabel(onlyGroupBy.label)}
                            </Text>
                        )
                    )}
                </>
            }
            legend={
                <ChartLegend
                    keys={legendKeys}
                    seriesMeta={result.seriesMeta}
                    selectedKeys={selectedKeys}
                    onToggleKey={handleToggleKey}
                />
            }
        >
            {renderChart ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0, padding: '12px 16px 16px' }}>
                    {renderChart({ result, selectedKeys })}
                </div>
            ) : (
                <StackedBarChart
                    {...result}
                    // Only a narrowed selection hides series; the series outside the legend are zero anyway.
                    selectedKeys={selectedKeys.length === legendKeys.length ? undefined : selectedKeys}
                    onBarClick={onBarClick}
                    tooltipSubtitle={tooltipSubtitle}
                />
            )}
        </ChartFrame>
    );
}
